"use client";

import {
  IconCalendarMonth,
  IconCreditCard,
  IconRosetteDiscountCheck,
} from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { DietInfoBadge } from "@/features/diets/components/diet-info-dialog/diet-info-badge";
import { DietInfoDialog } from "@/features/diets/components/diet-info-dialog/diet-info-dialog";
import { DietPaymentMethods } from "@/features/diets/components/diet-info-dialog/diet-payment-methods";

const Section = ({
  badge,
  children,
}: {
  badge: ReactNode;
  children: ReactNode;
}) => (
  <section className="space-y-2">
    <div>{badge}</div>
    {children}
  </section>
);

const Paragraph = ({ children }: { children: ReactNode }) => (
  <p className="text-sm leading-[22px]">{children}</p>
);

/**
 * "Delivery and payment" — what day the diet arrives on and how it can be paid
 * for, opened from the delivery row of the diet card.
 *
 * `deliveryDay` is the next possible delivery day resolved from the catering's
 * availability. The delivery time window is intentionally absent: the only
 * endpoint returning it (`/delivery-hours/{merchantId}`) requires a full
 * delivery address, which the configuration view does not have.
 */
export const DietDeliveryInfoDialog = ({
  deliveryDay,
  trigger,
}: {
  deliveryDay: string;
  trigger: ReactNode;
}) => {
  const t = useTranslations("diets.delivery");

  return (
    <DietInfoDialog title={t("title")} trigger={trigger}>
      <div className="space-y-4">
        <Section
          badge={
            <DietInfoBadge
              icon={<IconCalendarMonth className="size-5" stroke={1.5} />}
            >
              {t("dayBadge", { day: deliveryDay })}
            </DietInfoBadge>
          }
        >
          <Paragraph>{t("dayDescription", { day: deliveryDay })}</Paragraph>
        </Section>

        <hr className="border-brand-grey2-300" />

        <Section
          badge={
            <DietInfoBadge
              icon={
                <IconRosetteDiscountCheck className="size-5" stroke={1.5} />
              }
            >
              {t("qualityBadge")}
            </DietInfoBadge>
          }
        >
          <Paragraph>
            {t.rich("qualityDescription", {
              b: (chunks) => <strong className="font-medium">{chunks}</strong>,
            })}
          </Paragraph>
        </Section>

        <hr className="border-brand-grey2-300" />

        <Section
          badge={
            <DietInfoBadge
              icon={<IconCreditCard className="size-5" stroke={1.5} />}
            >
              {t("paymentsBadge")}
            </DietInfoBadge>
          }
        >
          <Paragraph>{t("paymentsDescription")}</Paragraph>
          <DietPaymentMethods />
        </Section>
      </div>
    </DietInfoDialog>
  );
};
